import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { emptyParticipant } from "../context/RegistrationContext";
import { participantsError, guardianError } from "../lib/registrationValidation";

async function checkReferral(guardian) {
  const code = (guardian.referral_code || "").trim();
  if (!code) return { ok: true, guardian: { ...guardian, referral_code: "", referral_partner: "" } };
  try {
    const { data } = await api.get(`/referral-codes/${encodeURIComponent(code)}/validate`);
    return { ok: true, guardian: { ...guardian, referral_code: data.code, referral_partner: data.partner } };
  } catch (e) {
    return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) };
  }
}

// Step state, participant list editing and per-step validation for the registration wizard.
export function useRegistrationWizard(reg, stepCount, refParam) {
  const { participants, setParticipants, guardian, setGuardian, consents, giftRecipientIndex, setGiftRecipientIndex } = reg;
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (refParam) { setGuardian((g) => (g.referral_code ? g : { ...g, referral_code: refParam.toUpperCase() })); }
  }, [refParam, setGuardian]);

  const update = (i, np) => setParticipants(participants.map((p, idx) => (idx === i ? np : p)));
  const add = () => { setParticipants([...participants, emptyParticipant()]); toast.success("Participant added"); };
  const remove = (i) => {
    if (participants.length === 1) return;
    setParticipants(participants.filter((_, idx) => idx !== i));
    if (giftRecipientIndex >= participants.length - 1) setGiftRecipientIndex(0);
  };

  const guardianStepError = async () => {
    const err = guardianError(guardian, consents);
    if (err) return err;
    const res = await checkReferral(guardian);
    if (res.ok) setGuardian(res.guardian);
    return res.ok ? null : res.error;
  };

  const stepError = async () => {
    if (step === 0) return participantsError(participants);
    if (step === 2) return guardianStepError();
    return null;
  };

  const go = (delta) => { setStep((s) => Math.min(Math.max(s + delta, 0), stepCount - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const next = async () => { const err = await stepError(); if (err) { toast.error(err); return; } go(1); };
  const back = () => go(-1);

  return { step, update, add, remove, next, back };
}
